/**
 * Tracking ID Tab Component
 * Displays and allows editing of participant tracking IDs
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Alert,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  ContentCopy,
  CheckCircle,
} from '@mui/icons-material';
import { ParticipantService } from '../../services/participantService';
import { apiService } from '../../config/api';
import type { Participant } from '../../types';
import { analyticsService } from '../../services/analyticsService';

interface TrackingIdTabProps {
  participants: Participant[];
  studyId: string;
  onRefresh: () => void;
}

const TrackingIdTab: React.FC<TrackingIdTabProps> = ({ participants, studyId, onRefresh }) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleEdit = (participant: Participant) => {
    setEditingId(participant.id);
    setEditValue(participant.trackingId || '');
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue('');
    setError(null);
    setSuccess(null);
  };

  const validateTrackingId = (id: string): boolean => {
    // Allow any characters except control characters
    // eslint-disable-next-line no-control-regex
    return /^[\s\S]*$/.test(id) && !/[\x00-\x1F\x7F]/.test(id);
  };

  const handleSave = async (participantId: string) => {
    const trimmedValue = editValue.trim();
    
    // Validate tracking ID (no control characters)
    if (trimmedValue && !validateTrackingId(trimmedValue)) {
      setError('Tracking ID cannot contain control characters');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      await ParticipantService.updateParticipant(
        participantId,
        { trackingId: trimmedValue || undefined },
        token
      );

      setSuccess('Tracking ID updated successfully');
      setEditingId(null);
      setEditValue('');
      analyticsService.trackTrackingIdEdited({
        studyId,
        hasTrackingId: !!trimmedValue
      });

      // Refresh the participants list
      setTimeout(() => {
        onRefresh();
        setSuccess(null);
      }, 1500);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.response?.data?.details?.[0]?.message || err.message || 'Failed to update tracking ID';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (trackingId: string) => {
    navigator.clipboard.writeText(trackingId);
    setCopiedId(trackingId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Filter participants with names (for display)
  const participantsWithNames = participants.filter(
    (p) => p.firstName || p.lastName
  );

  if (participantsWithNames.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          No Participants Available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Participants with names will appear here once they are enrolled.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Typography variant="h6" sx={{ mb: 2 }}>
        Participant Tracking IDs
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage tracking IDs for participants. You can customize tracking IDs as needed.
      </Typography>

      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 600,
                  borderBottom: '2px solid',
                  borderBottomColor: 'primary.main',
                }}
              >
                Participant Name
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  borderBottom: '2px solid',
                  borderBottomColor: 'primary.main',
                }}
              >
                Participant ID
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  borderBottom: '2px solid',
                  borderBottomColor: 'primary.main',
                }}
              >
                Tracking ID
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  borderBottom: '2px solid',
                  borderBottomColor: 'primary.main',
                  textAlign: 'center',
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {participantsWithNames.map((participant) => {
              const isEditing = editingId === participant.id;
              const displayName = `${participant.firstName || ''} ${participant.lastName || ''}`.trim() || 'N/A';

              return (
                <TableRow key={participant.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {displayName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {participant.participantId && (
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                      >
                        {participant.participantId}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={editValue}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (validateTrackingId(value) || value === '') {
                            setEditValue(value);
                            setError(null);
                          } else {
                            setError('Control characters are not allowed');
                          }
                        }}
                        placeholder="Enter tracking ID"
                        fullWidth
                        error={!!error}
                        helperText={error || 'Enter tracking ID'}
                        autoFocus
                        sx={{ maxWidth: 300 }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: participant.trackingId ? 500 : 400,
                            color: participant.trackingId ? 'text.primary' : 'text.secondary',
                          }}
                        >
                          {participant.trackingId || 'Not assigned'}
                        </Typography>
                        {participant.trackingId && (
                          <Tooltip title={copiedId === participant.trackingId ? 'Copied!' : 'Copy'}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(participant.trackingId!)}
                              sx={{ p: 0.5 }}
                            >
                              {copiedId === participant.trackingId ? (
                                <CheckCircle fontSize="small" color="success" />
                              ) : (
                                <ContentCopy fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {isEditing ? (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Save">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleSave(participant.id)}
                            disabled={loading}
                          >
                            <Save />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            size="small"
                            onClick={handleCancel}
                            disabled={loading}
                          >
                            <Cancel />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Tooltip title="Edit tracking ID">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(participant)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {participants.length > participantsWithNames.length && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Note: {participants.length - participantsWithNames.length} participant(s) without names are not shown.
        </Typography>
      )}
    </Box>
  );
};

export default TrackingIdTab;

